// QuestionFormFields.jsx - Fully English Version
import React from 'react';
import { Stack, TextField, FormControl, InputLabel, Select, MenuItem, Paper, Typography, Box, Button, IconButton } from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';

const SCALE_MIN_VALUE = 1;
const SCALE_MAX_VALUE = 10;

const clampScaleValue = (value) => Math.min(Math.max(value, SCALE_MIN_VALUE), SCALE_MAX_VALUE);

const QuestionFormFields = ({ 
  question, 
  setQuestion, 
  currentOptions, 
  setCurrentOptions,
  newOption,
  setNewOption,
  onQuestionTypeChange,
  isEdit = false 
}) => {
  const questionTypes = [
    { value: "MULTIPLE_CHOICE", label: "Multiple Choice" },
    { value: "SCALE", label: "Scale" },
    { value: "TEXT", label: "Text" },
    { value: "YES_NO", label: "Yes/No" },
    { value: "EMERGENCY_CONTACT", label: "Emergency Contact" }
  ];

  const addOption = () => {
    if (!newOption.text.trim()) {
      toast.error("Option text cannot be empty!");
      return;
    }
    
    // Backend will automatically assign value, so we only send text
    const newOptionData = {
      text: newOption.text.trim()
      // value will be assigned as index in backend
    };
    
    setCurrentOptions([...currentOptions, newOptionData]);
    setNewOption({ text: "" });
  };

  const removeOption = (index) => {
    const updatedOptions = currentOptions.filter((_, i) => i !== index);
    setCurrentOptions(updatedOptions);
  };

  const editOption = (index, field, value) => {
    const updatedOptions = currentOptions.map((option, i) => {
      if (i === index) {
        return { ...option, [field]: value };
      }
      return option;
    });
    setCurrentOptions(updatedOptions);
  };

  return (
    <Stack spacing={3} sx={{ mt: 1 }}>
      <TextField
        fullWidth
        label="Question Text *"
        multiline
        rows={2}
        value={question.questionText}
        onChange={(e) => setQuestion({...question, questionText: e.target.value})}
      />
      
      <TextField
        type="number"
        label="Question Number"
        value={question.questionNumber}
        onChange={(e) => setQuestion({...question, questionNumber: parseInt(e.target.value)})}
        sx={{ width: 200 }}
        inputProps={{ min: 1 }}
      />
      
      <FormControl fullWidth>
        <InputLabel>Question Type *</InputLabel>
        <Select
          value={question.questionType}
          label="Question Type *"
          onChange={(e) => onQuestionTypeChange(e.target.value, isEdit)}
        >
          {questionTypes.map((type) => (
            <MenuItem key={type.value} value={type.value}>
              {type.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      
      {/* Multiple Choice Options */}
      {question.questionType === "MULTIPLE_CHOICE" && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            Multiple Choice Options
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Options will be automatically scored from 0 to {Math.max(0, currentOptions.length - 1)} points based on order
          </Typography>
          
          {/* Current Options */}
          {currentOptions.map((option, index) => (
            <Box key={index} display="flex" alignItems="center" gap={2} mb={2}>
              <Typography variant="body2" sx={{ minWidth: 30, fontWeight: 'bold' }}>
                {String.fromCharCode(65 + index)})
              </Typography>
              <TextField
                fullWidth
                value={option.text || ""}
                onChange={(e) => editOption(index, "text", e.target.value)}
                size="small"
                placeholder="Option text"
              />
              {/* Value display (read-only) */}
              <Box 
                sx={{ 
                  minWidth: 60, 
                  textAlign: 'center', 
                  bgcolor: 'success.light', 
                  color: 'white',
                  p: 1, 
                  borderRadius: 1,
                  fontSize: '0.875rem',
                  fontWeight: 'bold'
                }}
              >
                {index} 
              </Box>
              <IconButton 
                color="error" 
                size="small" 
                onClick={() => removeOption(index)}
              >
                <DeleteIcon />
              </IconButton>
            </Box>
          ))}
          
          {/* Add New Option */}
          <Box display="flex" alignItems="center" gap={2} mt={2}>
            <Typography variant="body2" sx={{ minWidth: 30, fontWeight: 'bold' }}>
              {String.fromCharCode(65 + currentOptions.length)})
            </Typography>
            <TextField
              fullWidth
              placeholder="Enter option text"
              value={newOption.text}
              onChange={(e) => setNewOption({ text: e.target.value })}
              size="small"
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addOption();
                }
              }}
            />
            {/* Next value display */}
            <Box 
              sx={{ 
                minWidth: 60, 
                textAlign: 'center', 
                bgcolor: 'primary.main', 
                color: 'white',
                p: 1, 
                borderRadius: 1,
                fontSize: '0.875rem',
                fontWeight: 'bold'
              }}
            >
              {currentOptions.length}
            </Box>
            <Button 
              variant="outlined" 
              size="small" 
              onClick={addOption}
              disabled={!newOption.text.trim()}
            >
              Add
            </Button>
          </Box>
          
          {currentOptions.length === 0 && (
            <Typography variant="body2" color="error" sx={{ mt: 1 }}>
              Please add at least 2 options
            </Typography>
          )}
        </Paper>
      )}

      {/* YES_NO Options */}
      {question.questionType === "YES_NO" && (
        <Paper variant="outlined" sx={{ p: 2, bgcolor: "grey.50" }}>
          <Typography variant="subtitle1" gutterBottom>
            Yes/No Question (Automatic Options)
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>
            This question type will automatically create "Yes" and "No" options with fixed scoring
          </Typography>
          
          {/* YES_NO Preview */}
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" fontWeight="600" mb={1}>Preview:</Typography>
            <Box display="flex" alignItems="center" gap={2} mb={1}>
              <Typography variant="body2" sx={{ minWidth: 30, fontWeight: 'bold' }}>A)</Typography>
              <Typography variant="body2" sx={{ flex: 1 }}>Yes</Typography>
              <Box sx={{ 
                minWidth: 60, 
                textAlign: 'center', 
                bgcolor: 'success.main', 
                color: 'white', 
                p: 0.5, 
                borderRadius: 1, 
                fontSize: '0.75rem',
                fontWeight: 'bold'
              }}>
                1 pt
              </Box>
            </Box>
            <Box display="flex" alignItems="center" gap={2}>
              <Typography variant="body2" sx={{ minWidth: 30, fontWeight: 'bold' }}>B)</Typography>
              <Typography variant="body2" sx={{ flex: 1 }}>No</Typography>
              <Box sx={{ 
                minWidth: 60, 
                textAlign: 'center', 
                bgcolor: 'grey.400', 
                color: 'white', 
                p: 0.5, 
                borderRadius: 1, 
                fontSize: '0.75rem',
                fontWeight: 'bold'
              }}>
                0 pt
              </Box>
            </Box>
          </Box>
        </Paper>
      )}
      
      {/* Scale Settings */}
      {question.questionType === "SCALE" && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            Scale Question Settings
          </Typography>
          <Stack spacing={2}>
            <Box display="flex" gap={2}>
              <TextField
                label="Minimum Value"
                type="number"
                value={question.minScale}
                onChange={(e) => {
                  if (e.target.value === "") {
                    setQuestion({ ...question, minScale: "" });
                    return;
                  }
                  const parsed = Number(e.target.value);
                  if (Number.isNaN(parsed)) return;
                  setQuestion({
                    ...question,
                    minScale: clampScaleValue(Math.round(parsed))
                  });
                }}
                inputProps={{ min: SCALE_MIN_VALUE, max: SCALE_MAX_VALUE }}
              />
              <TextField
                label="Maximum Value"
                type="number"
                value={question.maxScale}
                onChange={(e) => {
                  if (e.target.value === "") {
                    setQuestion({ ...question, maxScale: "" });
                    return;
                  }
                  const parsed = Number(e.target.value);
                  if (Number.isNaN(parsed)) return;
                  setQuestion({
                    ...question,
                    maxScale: clampScaleValue(Math.round(parsed))
                  });
                }}
                inputProps={{ min: SCALE_MIN_VALUE, max: SCALE_MAX_VALUE }}
              />
            </Box>
            <Box display="flex" gap={2}>
              <TextField
                fullWidth
                label="Minimum Label"
                value={question.minLabel}
                onChange={(e) => setQuestion({...question, minLabel: e.target.value})}
                placeholder="e.g., Strongly Disagree"
              />
              <TextField
                fullWidth
                label="Maximum Label"
                value={question.maxLabel}
                onChange={(e) => setQuestion({...question, maxLabel: e.target.value})}
                placeholder="e.g., Strongly Agree"
              />
            </Box>
          </Stack>
          
          {/* Scale Preview */}
          {question.minScale && question.maxScale && question.minScale < question.maxScale && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
              <Typography variant="body2" fontWeight="600" mb={1}>Scale Preview:</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Typography variant="body2" sx={{ textAlign: 'left', minWidth: 80 }}>
                  {question.minLabel || `${question.minScale} (Min)`}
                </Typography>

                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.75,
                    flexWrap: 'wrap',
                    maxWidth: '100%',
                    overflow: 'visible',
                    rowGap: 0.75
                  }}
                >
                  {Array.from({ length: question.maxScale - question.minScale + 1 }, (_, i) => {
                    const value = question.minScale + i;
                    const bubbleSize = (question.maxScale - question.minScale + 1) > 8 ? 28 : 32;
                    return (
                      <Box 
                        key={i} 
                        sx={{ 
                          width: bubbleSize, 
                          height: bubbleSize, 
                          borderRadius: '50%', 
                          bgcolor: 'primary.main', 
                          color: 'white', 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center',
                          fontSize: bubbleSize > 30 ? '0.875rem' : '0.75rem',
                          fontWeight: 'bold'
                        }}
                      >
                        {value}
                      </Box>
                    );
                  })}
                </Box>

                <Typography variant="body2" sx={{ textAlign: 'right', minWidth: 80 }}>
                  {question.maxLabel || `${question.maxScale} (Max)`}
                </Typography>
              </Box>
            </Box>
          )}
        </Paper>
      )}

      {/* TEXT info */}
      {question.questionType === "TEXT" && (
        <Paper variant="outlined" sx={{ p: 2, bgcolor: "warning.light" }}>
          <Typography variant="subtitle1" gutterBottom>
            Open-ended Text Question
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Users will be able to enter free text responses. This question type is typically used for qualitative feedback and may require manual scoring or AI-assisted analysis.
          </Typography>
        </Paper>
      )}

      {/* EMERGENCY_CONTACT info */}
      {question.questionType === "EMERGENCY_CONTACT" && (
        <Paper variant="outlined" sx={{ p: 2, bgcolor: "error.light" }}>
          <Typography variant="subtitle1" gutterBottom fontWeight="bold">
            Emergency Contact Question
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>
            This type will display a fixed emergency contact form to the user. No additional configuration needed.
          </Typography>
          <Box sx={{ bgcolor: "white", borderRadius: 1, p: 2, border: "1px dashed #ccc" }}>
            <Typography variant="body2" fontWeight="600" mb={1}>Preview (shown to user):</Typography>
            <Typography variant="body2" mb={0.5}>Full Name: ___________________________</Typography>
            <Typography variant="body2" mb={0.5}>Relationship to you: __________________</Typography>
            <Typography variant="body2" mb={1.5}>Phone Number: ________________________</Typography>
            <Typography variant="body2" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box component="span" sx={{ border: "1px solid #555", width: 14, height: 14, display: "inline-block", flexShrink: 0 }} />
              I understand this contact will only be used in an emergency situation involving risk of harm.
            </Typography>
          </Box>
        </Paper>
      )}
    </Stack>
  );
};

export default QuestionFormFields;
